import { useDispatch } from "react-redux"
import React, { useEffect, useState, useRef } from "react"
import { TAgetUnregisteredBrands } from "../../../services/brandAPI"
import { DataTableSortStatus } from "mantine-datatable"
import sortBy from "lodash/sortBy"
import { setPageTitle } from "../../../redux/store/themeConfigSlice"
import { BrandTemp } from "../../../types/brandDatas"
import { useNavigate } from "react-router-dom"
import StickyDataTable from "../../../components/StickyDataTable"
import { isAbortError, getErrorResponse, getErrorStatus } from "../../../types/error"

const UnregisteredBrands = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const abortControllerRef = useRef<AbortController | null>(null)
  const [page, setPage] = useState(1)
  const PAGE_SIZES = [10, 20, 30, 50, 100, 500, 1000]
  const [totalRecords, setTotalRecords] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZES[2])
  const [initialRecords, setInitialRecords] = useState<BrandTemp[]>(sortBy({}, "id"))
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus>({
    columnAccessor: "id",
    direction: "asc",
  })
  const [error, setError] = useState<string | null>(null)
  const [sortByColumn, setSortByColumn] = useState("")
  const [sortOrder, setSortOrder] = useState(true)
  const [loading, setLoading] = useState(false)
  const [params, setParams] = useState({
    min_created_at: null as Date | null,
    max_created_at: null as Date | null,
    brand: "",
    sector: [""],
    sub_sector: [""],
    country: "",
  })

  useEffect(() => {
    dispatch(setPageTitle("Unregistered Brands"))
  })

  useEffect(() => {
    handleFetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortByColumn, sortOrder, page, pageSize, params])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  useEffect(() => {
    setPage(1)
  }, [pageSize])

  useEffect(() => {
    setSortStatus({
      columnAccessor: sortByColumn || "id",
      direction: sortOrder ? "desc" : "asc",
    })
  }, [sortByColumn, sortOrder])

  const handleSorting = (columnAccessor: string) => {
    if (columnAccessor === sortByColumn) {
      setSortOrder((prevOrder) => !prevOrder)
    } else {
      setSortByColumn(columnAccessor)
      setSortOrder(true)
    }
  }

  const handleFetchData = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    setLoading(true)
    setError(null)
    try {
      const newParams = {
        min_created_at: params.min_created_at ? params.min_created_at.toISOString() : "",
        max_created_at: params.max_created_at ? params.max_created_at.toISOString() : "",
        brand: params.brand,
        sector: params.sector.join(";"),
        sub_sector: params.sub_sector.join(";"),
        country: params.country,
      }
      const queryParams = new URLSearchParams(newParams as Record<string, string>).toString()
      const response = await TAgetUnregisteredBrands(
        sortByColumn,
        sortOrder,
        page,
        pageSize,
        queryParams,
        abortControllerRef.current.signal,
      )
      if (response && Array.isArray(response.unregisteredBrands)) {
        const totalRecords = response.totalDocuments
        const data = response.unregisteredBrands.map((item: BrandTemp) => {
          return {
            ...item,
          }
        })

        setInitialRecords(data)
        setTotalRecords(totalRecords)
        setLoading(false)
      } else {
        setLoading(false)
        setError("No data found")
      }
    } catch (error: unknown) {
      if (isAbortError(error)) {
        setLoading(false)
        return
      }

      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setLoading(false)
      setError(`Error fetching data: ${getErrorResponse(error) || "Unknown error"}`)
    }
  }

  const columns = [
    { accessor: "id", title: "Id" },
    {
      accessor: "firstName",
      title: "İsim",
      render: ({ first_name, last_name }: { first_name: string; last_name: string }) => (
        <div>{`${first_name || ""} ${last_name || ""}`}</div>
      ),
    },
    {
      accessor: "email",
      title: "Email",
      sortable: true,
    },
    {
      accessor: "phone",
      title: "Telefon",
      sortable: true,
    },
    {
      accessor: "brand_name",
      title: "Marka İsmi",
      sortable: true,
    },
    {
      accessor: "brand_category",
      title: "Marka Kategorisi",
      sortable: true,
    },
    {
      accessor: "sector",
      title: "Sektör",
      sortable: true,
    },
    {
      accessor: "sub_sector",
      title: "Alt Sektör",
      sortable: true,
    },
    {
      accessor: "country",
      title: "Ülke",
      sortable: true,
    },
    {
      accessor: "job_title",
      title: "İş Ünvanı",
      sortable: true,
    },
    {
      accessor: "heardFrom",
      title: "Nereden Duydunuz",
      sortable: true,
    },
    {
      accessor: "utm",
      title: "UTM",
      sortable: true,
      render: ({
        utm,
      }: {
        utm: { utm_source: string; utm_medium: string; utm_campaign: string }
      }) => {
        return (
          <>
            <div>{utm?.utm_source || "-"}</div>
            <div>{utm?.utm_medium || "-"}</div>
            <div>{utm?.utm_campaign || "-"}</div>
          </>
        )
      },
    },
    {
      accessor: "createdAt",
      title: "Oluşturulma Tarihi",
      sortable: true,
      render: ({ createdAt }: { createdAt: string }) => (
        <div>{createdAt ? new Date(createdAt).toLocaleDateString("tr-TR") : "-"}</div>
      ),
    },
  ]

  return (
    <div className="panel">
      <div className="mb-4.5 flex md:items-center md:flex-row flex-col gap-5">
        {error && (
          <div className="bg-red-200 text-red-800 border border-red-600 p-2 rounded">{error}</div>
        )}
      </div>

      <div className="datatables">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-pink-600"></div>
          </div>
        ) : (
          <StickyDataTable
            records={initialRecords}
            columns={columns}
            totalRecords={totalRecords}
            recordsPerPage={pageSize}
            page={page}
            onPageChange={(p) => setPage(p)}
            recordsPerPageOptions={PAGE_SIZES}
            onRecordsPerPageChange={setPageSize}
            sortStatus={sortStatus}
            onSortStatusChange={(e) => handleSorting(e.columnAccessor)}
            minHeight={200}
            paginationText={({ from, to, totalRecords }) =>
              `Showing  ${from} to ${to} of ${totalRecords} entries`
            }
            highlightOnHover={true}
            className="whitespace-nowrap"
          />
        )}
      </div>
    </div>
  )
}

export default UnregisteredBrands
